import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { localizePath, stripLangPrefix } from '../utils/i18nPath';

/**
 * useScrollToHash — returns a click handler that smooth-scrolls to a section
 * on the home page, navigating there first if the user is on a different route.
 *
 * Polls (rAF) up to ~2 s for the target element to mount, instead of relying
 * on a fixed setTimeout that races on slow devices/connections.
 *
 * The href argument is the canonical (UA) form — e.g. '/#about'. The hook
 * navigates to the language-appropriate home URL ('/' or '/en') automatically.
 *
 * Optional `onBeforeNavigate` callback fires before navigation (e.g. to close
 * the mobile menu).
 */
export const useScrollToHash = (onBeforeNavigate) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { language } = useLanguage();

    return useCallback(
        (e, href) => {
            e.preventDefault();
            if (typeof onBeforeNavigate === 'function') onBeforeNavigate();

            // Robust hash extraction: '/#about' / '#about' / '/en/#about' all → '#about'.
            const hashIdx = href.indexOf('#');
            if (hashIdx === -1) return;
            const hash = href.slice(hashIdx);
            const homePath = localizePath('/', language);
            const onHome = stripLangPrefix(location.pathname) === '/';

            const scrollTo = () => {
                const el = document.querySelector(hash);
                if (el) el.scrollIntoView({ behavior: 'smooth' });
                return Boolean(el);
            };

            if (onHome) {
                scrollTo();
                return;
            }

            // Navigate to localized home (include the hash so ScrollToTop
            // skips its top-scroll on this transition), then poll until the
            // section mounts.
            navigate(homePath + hash);
            const start = performance.now();
            const tryScroll = () => {
                if (scrollTo()) return;
                if (performance.now() - start < 2000) {
                    requestAnimationFrame(tryScroll);
                }
            };
            requestAnimationFrame(tryScroll);
        },
        [location.pathname, navigate, onBeforeNavigate, language]
    );
};

